import Dialog from "../../components/UI/Dialog";
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import {
  FiUsers,
  FiPlus,
  FiEdit,
  FiSettings,
  FiTarget,
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { Clan, ClanInvitation, ClanRequirements, ClanSettings, User, UserStats } from '../../types';
import { apiFetch } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { PageHeader, Tabs, ConfirmDialog } from '../../components/UI';
import { useLanguage } from '../../contexts/LanguageContext';
import { ClanOverview, ClanDetailsBody, InvitationsView, MyClanView, NoClan } from './ClansViews';

const ClansContainer = styled.div`
  width:100%; max-width:1320px; margin:0 auto; min-width:0;
  > header:first-child { margin-bottom:24px; @media (max-width:600px) { margin-bottom:18px; } }
  h1 { letter-spacing:-.035em; line-height:1.2; }
  h2,h3 { letter-spacing:-.02em; }
  button,input,textarea,select { font-size:14px; }
  input:focus-visible,textarea:focus-visible,select:focus-visible { border-color:${p=>p.theme.colors.primary}; box-shadow:0 0 0 3px ${p=>p.theme.colors.primarySoft}; }
`;






const ContentArea = styled.div`
  min-width: 0;
  margin-top: 16px;
`;






const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  min-width: 0;
  overflow-wrap: anywhere;
`;


const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-size:14px;
  display: block;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.5rem;
`;

const TextArea = styled.textarea.attrs({ maxLength: 1000 })`
  width: 100%;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-family: inherit;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text};
  }
`;

















// Новые стили для улучшенного интерфейса



























const FormSection = styled.div`
  margin-bottom: 0;
`;

const FormSectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  gap: 1rem;
  margin-bottom: 0;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.colors.text};
`;

const CheckboxLabel = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;






const EMPTY_CLAN_FORM = {
  name: '',
  tag: '',
  description: '',
  isPublic: true,
  maxMembers: 50,
  requirements: { minLevel: 1, minElo: 0, minWinRate: 0, requiredGames: 0, description: '' },
  settings: {
    allowInvites: true,
    autoAccept: false,
    requireApproval: true,
    allowMemberInvites: false,
    maxInvitesPerDay: 5,
    discordLink: '',
    website: '',
    rules: '',
  },
};

type ClanConfirm =
  | { kind: 'kick'; userId: string; username: string }
  | { kind: 'disband' }
  | { kind: 'leave' }
  | null;



function emptyUserStats(): UserStats {
  return {
    totalMatches: 0,
    wins: 0,
    losses: 0,
    winRate: 0,
    kd: 0,
    avgScore: 0,
    bestMap: '',
    favoriteWeapon: '',
  };
}

function mapBackendClanRow(raw: any): Clan {
  const l = raw.leader || {};
  const leader: User = {
    id: String(l.id || ''),
    username: String(l.username || '—'),
    email: '',
    level: 1,
    elo: typeof l.elo === 'number' ? l.elo : 1000,
    rank: '—',
    isOnline: false,
    isPremium: Boolean(l.isPremium),
    createdAt: String(l.createdAt || ''),
    stats: emptyUserStats(),
  };
  const reqRaw = raw.requirements;
  const requirements: ClanRequirements =
    reqRaw && typeof reqRaw === 'object'
      ? {
          minLevel: Number(reqRaw.minLevel) || 1,
          minElo: Number(reqRaw.minElo) || 0,
          minWinRate: Number(reqRaw.minWinRate) || 0,
          requiredGames: Number(reqRaw.requiredGames) || 0,
          description: String(reqRaw.description || ''),
        }
      : { minLevel: 1, minElo: 0, minWinRate: 0, requiredGames: 0, description: '' };
  const setRaw = raw.settings;
  const settings: ClanSettings =
    setRaw && typeof setRaw === 'object'
      ? {
          allowInvites: setRaw.allowInvites !== false,
          autoAccept: Boolean(setRaw.autoAccept),
          requireApproval: setRaw.requireApproval !== false,
          allowMemberInvites: Boolean(setRaw.allowMemberInvites),
          maxInvitesPerDay: Number(setRaw.maxInvitesPerDay) || 5,
          discordLink: String(setRaw.discordLink || ''),
          website: String(setRaw.website || ''),
          rules: String(setRaw.rules || ''),
        }
      : {
          allowInvites: true,
          autoAccept: false,
          requireApproval: true,
          allowMemberInvites: false,
          maxInvitesPerDay: 5,
          discordLink: '',
          website: '',
          rules: '',
        };
  const stat = raw.stats || {};
  return {
    id: String(raw.id),
    name: String(raw.name),
    tag: String(raw.tag),
    description: String(raw.description || ''),
    logo: '',
    banner: '',
    leader,
    members: Array.isArray(raw.members)
      ? raw.members.map((m: any) => ({
          user: mapPartialUser({
            id: m.id,
            username: m.username,
            elo: m.elo,
            isPremium: m.isPremium,
            createdAt: m.joinedAt,
          }),
          role: m.role === 'leader' ? 'leader' : 'member',
          joinedAt: String(m.joinedAt || ''),
        }))
      : [],
    maxMembers: typeof raw.maxMembers === 'number' ? raw.maxMembers : 50,
    memberCount: typeof raw.memberCount === 'number' ? raw.memberCount : undefined,
    isPublic: Boolean(raw.isPublic),
    createdAt: String(raw.createdAt || ''),
    stats: {
      totalMatches: Number(stat.totalMatches) || 0,
      wins: Number(stat.wins) || 0,
      losses: Number(stat.losses) || 0,
      winRate: Number(stat.winRate) || 0,
      averageElo: Number(stat.averageElo) || leader.elo,
    },
    requirements,
    settings,
  };
}

function clanMemberCount(c: Clan): number {
  return typeof c.memberCount === 'number' ? c.memberCount : c.members.length;
}

function mapPartialUser(raw: any): User {
  return {
    id: String(raw?.id || ''),
    username: String(raw?.username || '—'),
    email: String(raw?.email || ''),
    level: 1,
    elo: typeof raw?.elo === 'number' ? raw.elo : 1000,
    rank: '—',
    isOnline: false,
    isPremium: Boolean(raw?.isPremium),
    createdAt: String(raw?.createdAt || ''),
    stats: emptyUserStats(),
  };
}

function mapBackendInvitationRow(raw: any): ClanInvitation {
  return {
    id: String(raw.id),
    clan: mapBackendClanRow(raw.clan || {}),
    invitedBy: mapPartialUser(raw.invitedBy),
    invitedUser: mapPartialUser(raw.invitedUser),
    message: raw.message ? String(raw.message) : undefined,
    status: raw.status as ClanInvitation['status'],
    createdAt: String(raw.createdAt || ''),
    expiresAt: String(raw.expiresAt || ''),
  };
}

const Clans: React.FC = () => {
  const authUser = useAuthStore(s => s.user);
  const { t } = useLanguage();
  const [confirmAction, setConfirmAction] = useState<ClanConfirm>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [joiningId, setJoiningId] = useState('');
  const [myClanError, setMyClanError] = useState(false);
  const formBaseline = React.useRef('');
  const [activeTab, setActiveTab] = useState<'overview' | 'my-clan' | 'invitations'>('overview');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClan, setSelectedClan] = useState<Clan | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [myClan, setMyClan] = useState<Clan | null>(null);
  const [joinRequests, setJoinRequests] = useState<Array<{ id: string; username: string; elo: number; message: string }>>([]);
  const [pendingRequestClanIds, setPendingRequestClanIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'members' | 'winRate' | 'level'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterType] = useState<'all' | 'public' | 'private'>('all');
  
  // Расширенная форма создания/редактирования клана
  const [clanForm, setClanForm] = useState({
    name: '',
    tag: '',
    description: '',
    isPublic: true,
    maxMembers: 50,
    requirements: {
      minLevel: 1,
      minElo: 0,
      minWinRate: 0,
      requiredGames: 0,
      description: ''
    },
    settings: {
      allowInvites: true,
      autoAccept: false,
      requireApproval: true,
      allowMemberInvites: false,
      maxInvitesPerDay: 5,
      discordLink: '',
      website: '',
      rules: ''
    }
  });

  const [clans, setClans] = useState<Clan[]>([]);
  /* loading / ready / error различаются: пустой каталог и неудавшаяся загрузка — разные состояния. */
  const [clansStatus, setClansStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  /* Базовая линия фиксируется в момент открытия формы, а не после рендера: иначе нетронутая форма считается изменённой. */
  const startForm = (form: typeof EMPTY_CLAN_FORM) => {
    formBaseline.current = JSON.stringify(form);
    setClanForm(form);
  };
  const formDirty = JSON.stringify(clanForm) !== formBaseline.current;
  const [invitations, setInvitations] = useState<ClanInvitation[]>([]);
  const [clanRole, setClanRole] = useState<string | null>(null);
  const [inviteLogin, setInviteLogin] = useState('');

  const fetchClans = useCallback(async () => {
    try {
      const vis =
        filterType === "public" ? "public" : filterType === "private" ? "private" : "all";
      const params = new URLSearchParams({ page: "1", pageSize: "100" });
      if (vis !== "all") params.set("visibility", vis);
      const res = await apiFetch(`/clans?${params.toString()}`);
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setClans((data.items || []).map(mapBackendClanRow));
      setClansStatus('ready');
    } catch {
      // Уже загруженный список остаётся на экране; ошибка показывается блоком только при пустом каталоге.
      setClansStatus((prev) => (prev === 'ready' ? prev : 'error'));
      toast.error(t('cl.dir_error'));
    }
  }, [filterType, t]);

  useEffect(() => {
    void fetchClans();
  }, [fetchClans]);

  const fetchInvitations = useCallback(async () => {
    if (!authUser?.id) {
      setInvitations([]);
      return;
    }
    try {
      const res = await apiFetch('/clans/me/invitations');
      if (!res.ok) return;
      const data = await res.json();
      setInvitations((data.items || []).map(mapBackendInvitationRow));
    } catch {
      /* ignore */
    }
  }, [authUser?.id]);

  const refreshMyClan = useCallback(async () => {
    if (!authUser?.id) {
      setMyClan(null);
      setClanRole(null);
      return;
    }
    try {
      const res = await apiFetch('/clans/me');
      if (!res.ok) {
        setMyClanError(true);
        return;
      }
      setMyClanError(false);
      const data = await res.json();
      setClanRole(data.role ? String(data.role) : null);
      if (data?.clan) setMyClan(mapBackendClanRow(data.clan));
      else {
        setMyClan(null);
        setClanRole(null);
      }
    } catch {
      setMyClanError(true);
    }
  }, [authUser?.id]);

  const fetchJoinRequests = useCallback(async () => {
    if (!authUser?.id) {
      setJoinRequests([]);
      return;
    }
    try {
      const res = await apiFetch('/clans/me/requests');
      if (!res.ok) return;
      const data = await res.json();
      setJoinRequests(Array.isArray(data.items) ? data.items : []);
    } catch {
      /* ignore */
    }
  }, [authUser?.id]);

  useEffect(() => {
    void refreshMyClan();
  }, [refreshMyClan]);

  useEffect(() => {
    if (activeTab === 'invitations') void fetchInvitations();
    if (activeTab === 'my-clan') void fetchJoinRequests();
  }, [activeTab, fetchInvitations, fetchJoinRequests]);

  const handleAcceptJoinRequest = async (requestId: string) => {
    try {
      const res = await apiFetch(`/clans/requests/${requestId}/accept`, { method: 'POST' });
      if (res.ok) {
        toast.success('Игрок принят в клан');
        await Promise.all([fetchJoinRequests(), refreshMyClan()]);
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data?.error || 'Не удалось принять заявку');
      }
    } catch {
      toast.error('Ошибка сети');
    }
  };

  const askKick = (userId: string, username: string) =>
    setConfirmAction({ kind: 'kick', userId, username });

  const handleKickMember = async (userId: string, username: string) => {
    try {
      const res = await apiFetch(`/clans/me/members/${userId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success(`${username} исключён из клана`);
        await refreshMyClan();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data?.error || 'Не удалось исключить участника');
      }
    } catch {
      toast.error('Ошибка сети');
    }
  };

  const handleDeclineJoinRequest = async (requestId: string) => {
    try {
      const res = await apiFetch(`/clans/requests/${requestId}/decline`, { method: 'POST' });
      if (res.ok) {
        toast('Заявка отклонена');
        await fetchJoinRequests();
      } else {
        toast.error('Не удалось отклонить заявку');
      }
    } catch {
      toast.error('Ошибка сети');
    }
  };


  const handleCreateClan = async () => {
    if (!authUser?.id) {
      toast.error('Войдите, чтобы создать клан');
      return;
    }
    if (!clanForm.name || !clanForm.tag) {
      toast.error('Заполните все обязательные поля');
      return;
    }

    try {
      const res = await apiFetch('/clans', {
        method: 'POST',
        body: JSON.stringify({
          name: clanForm.name,
          tag: clanForm.tag,
          description: clanForm.description,
          isPublic: clanForm.isPublic,
          maxMembers: clanForm.maxMembers,
          requirements: clanForm.requirements,
          settings: clanForm.settings,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data?.error === 'string' ? data.error : 'Не удалось создать клан';
        toast.error(msg);
        return;
      }
      const created = data?.clan ? mapBackendClanRow(data.clan) : null;
      if (created) setMyClan(created);
      await fetchClans();
      await refreshMyClan();
      setShowCreateModal(false);
      setClanForm({
        name: '',
        tag: '',
        description: '',
        isPublic: true,
        maxMembers: 50,
        requirements: {
          minLevel: 1,
          minElo: 0,
          minWinRate: 0,
          requiredGames: 0,
          description: ''
        },
        settings: {
          allowInvites: true,
          autoAccept: false,
          requireApproval: true,
          allowMemberInvites: false,
          maxInvitesPerDay: 5,
          discordLink: '',
          website: '',
          rules: ''
        }
      });
      toast.success('Клан успешно создан!');
    } catch {
      toast.error('Не удалось создать клан');
    }
  };

  const handleEditClan = async () => {
    if (!authUser?.id) {
      toast.error('Войдите, чтобы редактировать клан');
      return;
    }
    if (!clanForm.name || !clanForm.tag || !myClan) {
      toast.error('Заполните все обязательные поля');
      return;
    }

    try {
      const res = await apiFetch('/clans/me', {
        method: 'PATCH',
        body: JSON.stringify({
          name: clanForm.name,
          tag: clanForm.tag,
          description: clanForm.description,
          isPublic: clanForm.isPublic,
          maxMembers: clanForm.maxMembers,
          requirements: clanForm.requirements,
          settings: clanForm.settings,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = typeof data?.error === 'string' ? data.error : 'Не удалось сохранить';
        toast.error(msg);
        return;
      }
      if (data?.clan) setMyClan(mapBackendClanRow(data.clan));
      await fetchClans();
      await refreshMyClan();
      setShowEditModal(false);
      toast.success('Клан успешно обновлен!');
    } catch {
      toast.error('Не удалось сохранить клан');
    }
  };

  const handleJoinClan = async (clanId: string) => {
    if (!authUser?.id) {
      toast.error('Войдите, чтобы вступить в клан');
      return;
    }
    if (joiningId || myClan) return;
    setJoiningId(clanId);
    try {
      const res = await apiFetch(`/clans/${clanId}/join`, {
        method: 'POST',
        body: JSON.stringify({ message: '' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data?.status === 'accepted') {
          toast.success('Вы вступили в клан!');
          if (data?.clan) setMyClan(mapBackendClanRow(data.clan));
        } else {
          toast.success('Заявка на вступление отправлена! Ожидайте решения лидера.');
        }
        setPendingRequestClanIds((prev) => (data?.status === 'pending' ? [...prev, clanId] : prev));
        await Promise.all([refreshMyClan(), fetchClans()]);
      } else {
        toast.error(data?.error || 'Не удалось отправить заявку');
      }
    } catch {
      toast.error('Ошибка сети при вступлении');
    } finally {
      setJoiningId('');
    }
  };

  const runConfirm = async () => {
    if (!confirmAction || confirmBusy) return;
    setConfirmBusy(true);
    try {
      if (confirmAction.kind === 'kick') {
        await handleKickMember(confirmAction.userId, confirmAction.username);
      } else if (confirmAction.kind === 'disband') {
        if (!authUser?.id) return;
        const res = await apiFetch('/clans/me', { method: 'DELETE' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(typeof data?.error === 'string' ? data.error : 'Не удалось распустить клан');
          return;
        }
        setMyClan(null);
        setClanRole(null);
        await fetchClans();
        toast.success('Клан распущен');
      } else {
        if (!authUser?.id) return;
        const res = await apiFetch('/clans/me/leave', { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(typeof data?.error === 'string' ? data.error : 'Ошибка');
          return;
        }
        setMyClan(null);
        setClanRole(null);
        await fetchClans();
        toast.success('Вы вышли из клана');
      }
      setConfirmAction(null);
    } catch {
      toast.error(t('common.error_generic'));
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleViewClan = (clanId: string) => {
    const clan = clans.find(c => c.id === clanId);
    if (clan) {
      setSelectedClan(clan);
      setShowDetailsModal(true);
    }
  };

  const handleViewClanFromInvitation = (clan: Clan) => {
    setSelectedClan(clan);
    setShowDetailsModal(true);
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    try {
      const res = await apiFetch(`/clans/invitations/${encodeURIComponent(invitationId)}/accept`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof data?.error === 'string' ? data.error : 'Ошибка');
        return;
      }
      toast.success('Вы вступили в клан');
      await fetchInvitations();
      await refreshMyClan();
      await fetchClans();
    } catch {
      toast.error('Не удалось принять приглашение');
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    try {
      const res = await apiFetch(`/clans/invitations/${encodeURIComponent(invitationId)}/decline`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof data?.error === 'string' ? data.error : 'Ошибка');
        return;
      }
      toast.success('Приглашение отклонено');
      await fetchInvitations();
    } catch {
      toast.error('Не удалось отклонить');
    }
  };

  const filteredClans = clans
    .filter(clan => {
      const matchesSearch = clan.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           clan.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           clan.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesFilter = filterType === 'all' || 
                           (filterType === 'public' && clan.isPublic) ||
                           (filterType === 'private' && !clan.isPublic);
      
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'members':
          aValue = clanMemberCount(a);
          bValue = clanMemberCount(b);
          break;
        case 'winRate':
          aValue = a.stats.winRate;
          bValue = b.stats.winRate;
          break;
        case 'level':
          aValue = a.leader.level;
          bValue = b.leader.level;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const openEditClan = () => {
    if (!myClan) return;
    startForm({
      name: myClan.name,
      tag: myClan.tag,
      description: myClan.description,
      isPublic: myClan.isPublic,
      maxMembers: myClan.maxMembers,
      requirements: {
        minLevel: myClan.requirements?.minLevel || 1,
        minElo: myClan.requirements?.minElo || 0,
        minWinRate: myClan.requirements?.minWinRate || 0,
        requiredGames: myClan.requirements?.requiredGames || 0,
        description: myClan.requirements?.description || '',
      },
      settings: {
        allowInvites: myClan.settings?.allowInvites ?? true,
        autoAccept: myClan.settings?.autoAccept ?? false,
        requireApproval: myClan.settings?.requireApproval ?? true,
        allowMemberInvites: myClan.settings?.allowMemberInvites ?? false,
        maxInvitesPerDay: myClan.settings?.maxInvitesPerDay || 5,
        discordLink: myClan.settings?.discordLink || '',
        website: myClan.settings?.website || '',
        rules: myClan.settings?.rules || '',
      },
    });
    setShowEditModal(true);
  };

  const sendClanInvite = async () => {
    if (!inviteLogin.trim()) {
      toast.error(t('cl.invite_placeholder'));
      return;
    }
    try {
      const res = await apiFetch('/clans/me/invitations', {
        method: 'POST',
        body: JSON.stringify({ usernameOrEmail: inviteLogin.trim(), message: '' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof data?.error === 'string' ? data.error : t('common.error_generic'));
        return;
      }
      setInviteLogin('');
      toast.success(t('cl.invite_sent'));
    } catch {
      toast.error(t('common.error_generic'));
    }
  };

  return (
    <ClansContainer>
      <PageHeader title={t('nav.clans')} />

      {myClanError && (
        <p role="alert" style={{ marginBottom: 12 }}>
          {t('clans.my_clan_error')}{' '}
          <button type="button" style={{ textDecoration: 'underline' }} onClick={() => void refreshMyClan()}>
            {t('common.retry')}
          </button>
        </p>
      )}

      <Tabs
        label={t('nav.clans')}
        value={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'overview' as const, label: t('clans.tab.overview') },
          { key: 'my-clan' as const, label: t('clans.tab.mine') },
          { key: 'invitations' as const, label: t('clans.tab.invitations'), badge: invitations.length ? <span>({invitations.length})</span> : undefined },
        ]}
      />

      <ContentArea>
        {activeTab === 'overview' && (
          <ClanOverview
            clans={filteredClans}
            query={searchQuery}
            onQuery={setSearchQuery}
            sortBy={sortBy}
            onSortBy={setSortBy}
            sortOrder={sortOrder}
            onSortOrder={setSortOrder}
            myClan={myClan}
            pendingIds={pendingRequestClanIds}
            joiningId={joiningId}
            empty={clans.length === 0}
            status={clansStatus}
            onRetry={() => {
              setClansStatus('loading');
              void fetchClans();
            }}
            onView={(id) => handleViewClan(id)}
            onJoin={handleJoinClan}
            onCreate={() => {
              startForm(EMPTY_CLAN_FORM);
              setShowCreateModal(true);
            }}
          />
        )}

        {activeTab === 'my-clan' &&
          (myClan ? (
            <MyClanView
              clan={myClan}
              role={clanRole}
              userId={authUser?.id}
              joinRequests={joinRequests}
              inviteLogin={inviteLogin}
              onInviteLogin={setInviteLogin}
              onInvite={sendClanInvite}
              onAcceptJoin={handleAcceptJoinRequest}
              onDeclineJoin={handleDeclineJoinRequest}
              onKick={askKick}
              onEdit={openEditClan}
              onLeave={() => setConfirmAction({ kind: 'leave' })}
              onDisband={() => setConfirmAction({ kind: 'disband' })}
            />
          ) : (
            <NoClan
              onFind={() => setActiveTab('overview')}
              onCreate={() => {
                startForm(EMPTY_CLAN_FORM);
                setShowCreateModal(true);
              }}
            />
          ))}

        {activeTab === 'invitations' && (
          <InvitationsView
            invitations={invitations}
            onAccept={handleAcceptInvitation}
            onDecline={handleDeclineInvitation}
            onView={handleViewClanFromInvitation}
            onOverview={() => setActiveTab('overview')}
          />
        )}
      </ContentArea>

      {/* Модальное окно создания клана */}
      <AnimatePresence>
        {showCreateModal && (
          <Dialog
            wide
            label="Создать клан"
            onClose={() => setShowCreateModal(false)}
            dirty={formDirty}
            footer={
              <>
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Отмена
                </Button>
                <Button onClick={handleCreateClan}>
                  <FiPlus size={16} />
                  Создать клан
                </Button>
              </>
            }
          >
              <ModalHeader>
                <ModalTitle>Создать клан</ModalTitle>
              </ModalHeader>

              <FormSection>
                <FormSectionTitle>
                  <FiUsers size={20} />
                  Основная информация
                </FormSectionTitle>
                
                <FormRow>
                  <FormGroup>
                    <Label>Название клана *</Label>
                    <Input
                      value={clanForm.name}
                      onChange={(e) => setClanForm({ ...clanForm, name: e.target.value })}
                      placeholder="Введите название клана"
                      maxLength={30}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Тег клана * (только в игре)</Label>
                    <Input
                      value={clanForm.tag}
                      onChange={(e) => setClanForm({ ...clanForm, tag: e.target.value })}
                      placeholder="Например: [TAG]"
                      maxLength={10}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Описание</Label>
                  <TextArea
                    value={clanForm.description}
                    onChange={(e) => setClanForm({ ...clanForm, description: e.target.value })}
                    placeholder="Опишите ваш клан..."
                    style={{ minHeight: '120px' }}
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <Label>Максимум участников</Label>
                    <Input
                      type="number"
                      value={clanForm.maxMembers}
                      onChange={(e) => setClanForm({ ...clanForm, maxMembers: Math.min(100, Math.max(5, parseInt(e.target.value) || 5)) })}
                      placeholder="50"
                    />
                  </FormGroup>
                  <FormGroup>
                    <CheckboxGroup>

                    </CheckboxGroup>
                  </FormGroup>
                </FormRow>
              </FormSection>

              <FormSection>
                <FormSectionTitle>
                  <FiTarget size={20} />
                  Требования для вступления
                </FormSectionTitle>
                
                <FormRow>
                  <FormGroup>
                    <Label>Минимальный уровень</Label>
                    <Input
                      type="number"
                      value={clanForm.requirements.minLevel}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        requirements: { ...clanForm.requirements, minLevel: Math.min(10, Math.max(1, parseInt(e.target.value) || 1)) }
                      })}
                      placeholder="1"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Минимальный ELO</Label>
                    <Input
                      type="number"
                      value={clanForm.requirements.minElo}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        requirements: { ...clanForm.requirements, minElo: Math.min(9999, Math.max(0, parseInt(e.target.value) || 0)) }
                      })}
                      placeholder="0"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Минимальный % побед</Label>
                    <Input
                      type="number"
                      value={clanForm.requirements.minWinRate}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        requirements: { ...clanForm.requirements, minWinRate: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }
                      })}
                      placeholder="0"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Минимум игр</Label>
                    <Input
                      type="number"
                      value={clanForm.requirements.requiredGames}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        requirements: { ...clanForm.requirements, requiredGames: Math.min(9999, Math.max(0, parseInt(e.target.value) || 0)) }
                      })}
                      placeholder="0"
                    />
                  </FormGroup>
                </FormRow>

              </FormSection>

              <FormSection>
                <FormSectionTitle>
                  <FiSettings size={20} />
                  Настройки клана
                </FormSectionTitle>
                
                <CheckboxGroup>


                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      checked={clanForm.settings.requireApproval}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        settings: { ...clanForm.settings, requireApproval: e.target.checked }
                      })}
                    />
                    <CheckboxLabel>Требовать одобрение лидера</CheckboxLabel>
                  </CheckboxItem>
                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      checked={clanForm.settings.allowMemberInvites}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        settings: { ...clanForm.settings, allowMemberInvites: e.target.checked }
                      })}
                    />
                    <CheckboxLabel>Участники могут приглашать</CheckboxLabel>
                  </CheckboxItem>
                </CheckboxGroup>

                <FormRow>

                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Discord сервер</Label>
                    <Input
                      value={clanForm.settings.discordLink}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        settings: { ...clanForm.settings, discordLink: e.target.value }
                      })}
                      placeholder="https://discord.gg/..."
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Telegram</Label>
                    <Input
                      value={clanForm.settings.website}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        settings: { ...clanForm.settings, website: e.target.value }
                      })}
                      placeholder="https://t.me/..."
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Правила клана</Label>
                  <TextArea
                    value={clanForm.settings.rules}
                    onChange={(e) => setClanForm({ 
                      ...clanForm, 
                      settings: { ...clanForm.settings, rules: e.target.value }
                    })}
                    placeholder="Опишите правила вашего клана..."
                    style={{ minHeight: '100px' }}
                  />
                </FormGroup>
              </FormSection>

            </Dialog>
        )}
      </AnimatePresence>

      {/* Модальное окно редактирования клана */}
      <AnimatePresence>
        {showEditModal && (
          <Dialog
            wide
            label="Редактировать клан"
            onClose={() => setShowEditModal(false)}
            dirty={formDirty}
            footer={
              <>
                <Button variant="outline" onClick={() => setShowEditModal(false)}>
                  Отмена
                </Button>
                <Button onClick={handleEditClan}>
                  <FiEdit size={16} />
                  Сохранить изменения
                </Button>
              </>
            }
          >
              <ModalHeader>
                <ModalTitle>Редактировать клан</ModalTitle>
              </ModalHeader>

              <FormSection>
                <FormSectionTitle>
                  <FiUsers size={20} />
                  Основная информация
                </FormSectionTitle>
                
                <FormRow>
                  <FormGroup>
                    <Label>Название клана *</Label>
                    <Input
                      value={clanForm.name}
                      onChange={(e) => setClanForm({ ...clanForm, name: e.target.value })}
                      placeholder="Введите название клана"
                      maxLength={30}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Тег клана * (только в игре)</Label>
                    <Input
                      value={clanForm.tag}
                      onChange={(e) => setClanForm({ ...clanForm, tag: e.target.value })}
                      placeholder="Например: [TAG]"
                      maxLength={10}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Описание</Label>
                  <TextArea
                    value={clanForm.description}
                    onChange={(e) => setClanForm({ ...clanForm, description: e.target.value })}
                    placeholder="Опишите ваш клан..."
                    style={{ minHeight: '120px' }}
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <Label>Максимум участников</Label>
                    <Input
                      type="number"
                      value={clanForm.maxMembers}
                      onChange={(e) => setClanForm({ ...clanForm, maxMembers: Math.min(100, Math.max(5, parseInt(e.target.value) || 5)) })}
                      placeholder="50"
                    />
                  </FormGroup>
                  <FormGroup>
                    <CheckboxGroup>

                    </CheckboxGroup>
                  </FormGroup>
                </FormRow>
              </FormSection>

              <FormSection>
                <FormSectionTitle>
                  <FiTarget size={20} />
                  Требования для вступления
                </FormSectionTitle>
                
                <FormRow>
                  <FormGroup>
                    <Label>Минимальный уровень</Label>
                    <Input
                      type="number"
                      value={clanForm.requirements.minLevel}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        requirements: { ...clanForm.requirements, minLevel: Math.min(10, Math.max(1, parseInt(e.target.value) || 1)) }
                      })}
                      placeholder="1"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Минимальный ELO</Label>
                    <Input
                      type="number"
                      value={clanForm.requirements.minElo}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        requirements: { ...clanForm.requirements, minElo: Math.min(9999, Math.max(0, parseInt(e.target.value) || 0)) }
                      })}
                      placeholder="0"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Минимальный % побед</Label>
                    <Input
                      type="number"
                      value={clanForm.requirements.minWinRate}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        requirements: { ...clanForm.requirements, minWinRate: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) }
                      })}
                      placeholder="0"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Минимум игр</Label>
                    <Input
                      type="number"
                      value={clanForm.requirements.requiredGames}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        requirements: { ...clanForm.requirements, requiredGames: Math.min(9999, Math.max(0, parseInt(e.target.value) || 0)) }
                      })}
                      placeholder="0"
                    />
                  </FormGroup>
                </FormRow>

              </FormSection>

              <FormSection>
                <FormSectionTitle>
                  <FiSettings size={20} />
                  Настройки клана
                </FormSectionTitle>
                
                <CheckboxGroup>


                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      checked={clanForm.settings.requireApproval}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        settings: { ...clanForm.settings, requireApproval: e.target.checked }
                      })}
                    />
                    <CheckboxLabel>Требовать одобрение лидера</CheckboxLabel>
                  </CheckboxItem>
                  <CheckboxItem>
                    <Checkbox
                      type="checkbox"
                      checked={clanForm.settings.allowMemberInvites}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        settings: { ...clanForm.settings, allowMemberInvites: e.target.checked }
                      })}
                    />
                    <CheckboxLabel>Участники могут приглашать</CheckboxLabel>
                  </CheckboxItem>
                </CheckboxGroup>

                <FormRow>

                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Discord сервер</Label>
                    <Input
                      value={clanForm.settings.discordLink}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        settings: { ...clanForm.settings, discordLink: e.target.value }
                      })}
                      placeholder="https://discord.gg/..."
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Telegram</Label>
                    <Input
                      value={clanForm.settings.website}
                      onChange={(e) => setClanForm({ 
                        ...clanForm, 
                        settings: { ...clanForm.settings, website: e.target.value }
                      })}
                      placeholder="https://t.me/..."
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Правила клана</Label>
                  <TextArea
                    value={clanForm.settings.rules}
                    onChange={(e) => setClanForm({ 
                      ...clanForm, 
                      settings: { ...clanForm.settings, rules: e.target.value }
                    })}
                    placeholder="Опишите правила вашего клана..."
                    style={{ minHeight: '100px' }}
                  />
                </FormGroup>
              </FormSection>

            </Dialog>
        )}
      </AnimatePresence>

      {/* Детали клана */}
      <AnimatePresence>
        {showDetailsModal && selectedClan && (
          <Dialog
            wide
            label={t('cl.details')}
            onClose={() => setShowDetailsModal(false)}
            footer={
              <>
                <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                  {t('cl.close')}
                </Button>
                {myClan ? (
                  <span style={{ fontSize: 14, opacity: 0.8 }}>{t('clans.already_member', { name: myClan.name })}</span>
                ) : !selectedClan.isPublic ? (
                  <span style={{ fontSize: 14, opacity: 0.8 }}>{t('cl.invite_only')}</span>
                ) : clanMemberCount(selectedClan) >= selectedClan.maxMembers ? (
                  <span style={{ fontSize: 14, opacity: 0.8 }}>{t('cl.full')}</span>
                ) : (
                  <Button
                    loading={joiningId === selectedClan.id}
                    disabled={!!joiningId}
                    onClick={() => {
                      handleJoinClan(selectedClan.id);
                      setShowDetailsModal(false);
                    }}
                  >
                    {selectedClan.settings?.autoAccept ? t('cl.join') : t('cl.apply')}
                  </Button>
                )}
              </>
            }
          >
            <ClanDetailsBody clan={selectedClan} />
          </Dialog>
        )}
      </AnimatePresence>
      {confirmAction && (
        <ConfirmDialog
          title={
            confirmAction.kind === 'kick'
              ? t('clans.confirm.kick_title', { name: confirmAction.username })
              : confirmAction.kind === 'disband'
                ? t('clans.confirm.disband_title', { name: myClan?.name || '' })
                : t('clans.confirm.leave_title', { name: myClan?.name || '' })
          }
          description={
            confirmAction.kind === 'disband'
              ? t('clans.confirm.disband_text')
              : confirmAction.kind === 'leave'
                ? t('clans.confirm.leave_text')
                : t('clans.confirm.kick_text')
          }
          confirmLabel={
            confirmAction.kind === 'kick'
              ? t('clans.confirm.kick')
              : confirmAction.kind === 'disband'
                ? t('clans.confirm.disband')
                : t('clans.confirm.leave')
          }
          requireText={confirmAction.kind === 'disband' ? myClan?.name : undefined}
          busy={confirmBusy}
          onConfirm={runConfirm}
          onClose={() => setConfirmAction(null)}
        />
      )}
    </ClansContainer>
  );
};

export default Clans;